import { useState } from 'react';
import { useCan } from '../../lib/auth/session.js';
import { sipUsernameFor } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { toastError } from '../../components/ui/Toast.js';

/**
 * What a phone signs in with: username, domain and password, as one list.
 *
 * The username and domain are not secrets, so they are always shown. The
 * username used to appear only once the password was revealed, when it is the
 * first thing somebody types into a phone (15 September 2026).
 *
 * The password is shown on demand, or replaced. Both are gated on
 * `extension:credentials:reveal`, which sits in the step-up list, so the API
 * can refuse with a step-up challenge even though the session is perfectly
 * valid. That is not an error state to report; it is a question to ask. This
 * component asks it inline and retries, so revealing a password is one
 * interaction rather than a dead end and a trip elsewhere.
 */

interface Credential {
  username: string;
  /** The bare number, while it still signs in with this password (docs/26 E8). */
  legacy_username?: string | null;
  realm: string;
  password: string | null;
  recoverable?: boolean;
  reason?: string;
}

export function SipCredential({ tenantId, extensionId, number, domain, server }: {
  tenantId: string; extensionId: string; number: string;
  /** The account's SIP domain, once it has loaded. */
  domain: string | null;
  /** Where the phone sends its SIP: every account's registration host. */
  server?: string | null;
}) {
  const [cred, setCred] = useState<Credential | null>(null);
  const [busy, setBusy] = useState(false);
  const mayReveal = useCan()('extension:credentials:reveal');
  const [what, setWhat] = useState<'reveal' | 'rotate'>('reveal');
  /**
   * The challenge lives in the shared hook now. This component asked correctly
   * and the balance form did not, so the asking was lifted out rather than
   * copied across (ADR-019) -- there is one implementation of "prove it, then
   * carry on with what you were doing".
   */
  const step = useStepUp();

  const base = `/v1/tenants/${tenantId}/extensions/${extensionId}/credential`;

  const run = async (which: 'reveal' | 'rotate') => {
    setWhat(which);
    setBusy(true);
    try {
      const r = await step.run(() => which === 'reveal'
        ? api.get<Credential>(base)
        : api.post<Credential>(`${base}/rotate`, {}));
      // undefined means the hook raised a challenge and is holding the action.
      if (r !== undefined) setCred(r);
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };

  // What was revealed is the truth; until then, the rule the API itself uses
  // to name a seat's username (contracts sipUsernameFor, docs/26 E8).
  const username = cred?.username ?? (domain ? sipUsernameFor(number, domain) : null);
  const realm = cred?.realm ?? domain;

  return (
    <>
      <StepUpPrompt step={step}
        what={what === 'rotate' ? 'Issuing a new SIP password' : 'Reading a SIP password'} />

      <dl className="arc-kv">
        <dt>Username</dt>
        <dd className="arc-mono">{username ?? '\u2014'}</dd>

        <dt>Domain</dt>
        <dd>
          <span className="arc-mono">{realm ?? '\u2014'}</span>
          {/* The switch takes the digest realm from the domain a phone signs in
              to, so a phone given the node's IP address is refused even with
              the right password -- the first thing that went wrong when a
              softphone was set up by hand (docs/26 A1). */}
          <span className="arc-subtle" style={{ marginLeft: 10 }}>the domain, not an IP address</span>
        </dd>

        {server && (
          <>
            {/* Where the phone sends its SIP, the same for every account (26 September 2026): a
                softphone's "outbound proxy" or "server" field. The domain above stays what it signs
                in as. */}
            <dt>Server</dt>
            <dd>
              <span className="arc-mono">{server}</span>
              <span className="arc-subtle" style={{ marginLeft: 10 }}>as the outbound proxy, port 5060</span>
            </dd>
          </>
        )}

        <dt>Password</dt>
        <dd>
          <span className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {cred?.password
              ? <span className="arc-mono" style={{ fontWeight: 600, userSelect: 'all' }}>{cred.password}</span>
              : cred
                /* Imported credentials have only a digest behind them, which
                   cannot be reversed. Saying so is more useful than an error. */
                ? <span className="arc-subtle">{cred.reason ?? 'Not recoverable'}</span>
                : <span className="arc-mono arc-subtle" aria-label="Hidden">{'\u2022'.repeat(10)}</span>}
            {cred?.password && (
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                onClick={() => void navigator.clipboard?.writeText(cred.password!)}>
                Copy
              </button>
            )}
            {/* Both return the password, so both need extension:credentials:reveal (permission sweep, 19 September 2026). */}
            {mayReveal && <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
              disabled={busy} onClick={() => void run('reveal')}>
              {cred ? 'Show again' : 'Show'}
            </button>}
            {mayReveal && <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-btn-danger-ghost"
              disabled={busy}
              onClick={() => {
                if (confirm(
                  `Issue a new SIP password for ${number}?\n\n`
                  + 'The handset using the current password will stop registering '
                  + 'within about a minute and has to be reconfigured.')) {
                  void run('rotate');
                }
              }}>
              Issue a new password
            </button>}
          </span>
        </dd>
      </dl>

      {cred?.legacy_username && (
        <p className="arc-hint" style={{ margin: '10px 0 0' }}>
          Phones still signed in as {cred.legacy_username} keep working until a new password is issued.
        </p>
      )}
    </>
  );
}
